import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { useProjects } from '../../hooks/useProjects.jsx'
import { userRoles } from '../../data/mockData'
import { formatDate } from '../../utils/helpers'
import { useTranslation } from 'react-i18next'

function FundingApproval() {
  const { t } = useTranslation('money')
  const { t: tc } = useTranslation('common')
  const { user } = useAuth()
  const { projects, approveProject, rejectProject } = useProjects()
  const navigate = useNavigate()

  const [selectedStatus, setSelectedStatus] = useState('Pending Approval')
  const [selectedRequest, setSelectedRequest] = useState(null)
  const [showApprovalModal, setShowApprovalModal] = useState(false)
  const [submitting, setSubmitting] = useState(false)        // guards modal submit
  const [rejectingId, setRejectingId] = useState(null)        // guards quick-reject
  const [approvalData, setApprovalData] = useState({
    decision: 'approve',
    comments: '',
    conditions: '',
    approvedAmount: 0
  })

  const canApprove = user?.role === userRoles.ADMIN || user?.role === userRoles.Funder || user?.role === userRoles.FINANCE

  if (!canApprove) {
    return (
      <div className="p-6">
        <div className="card">
          <div className="p-12 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2-2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <h3 className="mt-4 text-lg font-medium text-gray-900">{t('access_restricted')}</h3>
            <p className="mt-2 text-gray-600">{t('no_perm_funding')}</p>
          </div>
        </div>
      </div>
    )
  }

  // Filter projects based on status and ensuring they are in a "funding relevant" state
  // We consider 'Pending Approval', 'Planning' (Approved), 'Rejected' as relevant statuses
  const relevantProjects = projects.filter(p =>
    p.status === 'Pending Approval' || p.status === 'Planning' || p.status === 'Rejected' || p.fundingStatus
  );

  const filteredRequests = relevantProjects.filter(project => {
    if (selectedStatus === 'All') return true;
    // Map 'Approved' filter to 'Planning' or 'Approved' funding status
    if (selectedStatus === 'Approved') return project.status === 'Planning' || project.fundingStatus === 'Approved';
    return project.status === selectedStatus;
  });

  const handleApproval = (project) => {
    setSelectedRequest(project)
    setApprovalData({
      decision: 'approve',
      comments: '',
      conditions: '',
      approvedAmount: project.totalBudget || 0
    })
    setShowApprovalModal(true)
  }

  const submitApproval = async () => {
    if (!selectedRequest || submitting) return;   // ignore repeat clicks while in flight
    setSubmitting(true)
    try {
      if (approvalData.decision === 'approve' || approvalData.decision === 'partial') {
        await approveProject(selectedRequest.id);
      } else {
        await rejectProject(selectedRequest.id);
      }
      setShowApprovalModal(false)
      setSelectedRequest(null)
    } finally {
      setSubmitting(false)
    }
  }

  const quickReject = async (projectId) => {
    if (rejectingId) return;
    setRejectingId(projectId)
    try {
      await rejectProject(projectId)
    } finally {
      setRejectingId(null)
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'Pending Approval': return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      case 'under Review': return 'bg-blue-100 text-blue-800 border-blue-200'
      case 'Planning': // Synonymous with Approved for funding
      case 'Approved': return 'bg-green-100 text-green-800 border-green-200'
      case 'Rejected': return 'bg-red-100 text-red-800 border-red-200'
      default: return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High': return 'bg-red-100 text-red-800'
      case 'Medium': return 'bg-yellow-100 text-yellow-800'
      case 'Low': return 'bg-green-100 text-green-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const approvalStats = {
    pending: relevantProjects.filter(r => r.status === 'Pending Approval').length,
    underReview: relevantProjects.filter(r => r.status === 'under Review').length,
    approved: relevantProjects.filter(r => r.status === 'Planning' || r.fundingStatus === 'Approved').length,
    totalValue: relevantProjects
      .filter(r => r.status === 'Pending Approval')
      .reduce((sum, r) => sum + (r.totalBudget || 0), 0)
  }

  return (
    <div className="p-6 space-y-6">
      {/* header */}
      <div className="bg-gradient-to-r from-green-600 to-teal-600 rounded-xl p-6 text-white shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">{t('funding_approval_center')}</h1>
            <p className="mt-1 opacity-90">{t('review_and_approve')}</p>
          </div>
          <div className="text-right hidden sm:block">
            <p className="text-sm opacity-90">{t('st_pending_approval')}</p>
            <p className="text-3xl font-bold">{approvalStats.pending}</p>
            <p className="text-sm opacity-90">${approvalStats.totalValue.toLocaleString()} total value</p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-yellow-100">
              <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{t('pending_requests')}</p>
              <p className="text-2xl font-bold text-gray-900">{approvalStats.pending}</p>
              <p className="text-xs text-yellow-600">{t('awaiting_review')}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v6a2 2 0 002 2h2m5 0h2a2 2 0 002-2V7a2 2 0 00-2-2h-2m-5-4v4m0 0l3-3m-3 3L9 9" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">under Review</p>
              <p className="text-2xl font-bold text-gray-900">{approvalStats.underReview}</p>
              <p className="text-xs text-blue-600">{t('st_in_progress')}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{t('st_approved')}</p>
              <p className="text-2xl font-bold text-gray-900">{approvalStats.approved}</p>
              <p className="text-xs text-green-600">{t('this_month')}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{t('total_value')}</p>
              <p className="text-2xl font-bold text-gray-900">${approvalStats.totalValue.toLocaleString()}</p>
              <p className="text-xs text-purple-600">{t('pending_approval_txt')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center space-x-4 w-full sm:w-auto">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">{t('filter_by_status')}</label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full sm:w-48 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="Pending Approval">{t('st_pending_approval')}</option>
                <option value="Approved">{t('st_approved')}</option>
                <option value="Rejected">{t('st_rejected')}</option>
                <option value="All">{t('all_requests')}</option>
              </select>
            </div>
            <div className="text-sm text-gray-500">
              Showing {filteredRequests.length} requests
            </div>
          </div>
        </div>
      </div>

      {/* Approval Queue */}
      <div className="space-y-6">
        {filteredRequests.map((project) => {
          return (
            <div key={project.id} className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="p-6">
                <div className="flex flex-col md:flex-row md:items-start justify-between mb-6 gap-4">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <h3 className="text-xl font-bold text-gray-900">{project.name}</h3>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(project.status)}`}>
                        {project.status === 'Planning' ? 'Approved' : project.status}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(project.priority)}`}>
                        {project.priority || 'Medium'} Priority
                      </span>
                    </div>
                    <p className="text-gray-700 mb-4 leading-relaxed">{project.description}</p>
                    {project.description && (
                      <div className="bg-gray-50 rounded-lg p-3 inline-block">
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('project_description')}</span>
                        <p className="text-sm text-gray-800 mt-1 max-w-lg">{project.description}</p>
                      </div>
                    )}
                  </div>
                  <div className="text-left md:text-right md:ml-6">
                    <p className="text-3xl font-bold text-gray-900">${(project.totalBudget || project.total_budget || 0).toLocaleString()}</p>
                    <p className="text-sm text-gray-500">{t('requested_amount')}</p>
                    {/* placeholder for ROI as it's not in the main project model yet */}
                    <p className="text-sm font-medium text-blue-600 mt-1">{t('expected_roi_sample')}</p>
                  </div>
                </div>

                {/* Request details Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs font-medium text-gray-600 mb-1">{t('requested_by')}</p>
                    <p className="text-sm font-semibold text-gray-900">{project.projectManager || 'Project Manager'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs font-medium text-gray-600 mb-1">{tc('labels.start_date')}</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {(project.startDate || project.start_date)
                        ? formatDate(project.startDate || project.start_date)
                        : 'Not set'}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs font-medium text-gray-600 mb-1">{tc('labels.end_date')}</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {(project.endDate || project.end_date)
                        ? formatDate(project.endDate || project.end_date)
                        : 'Not set'}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs font-medium text-gray-600 mb-1">{tc('labels.category')}</p>
                    <p className="text-sm font-semibold text-gray-900">{project.category || 'General'}</p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between pt-6 border-t border-gray-200 gap-4">
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-500">
                    <span>Project ID: #{project.id}</span>
                    <span className="hidden sm:inline">•</span>
                    <span>Created: {project.createdAt ? formatDate(project.createdAt) : 'Recently'}</span>
                  </div>
                  <div className="flex flex-wrap gap-3 w-full sm:w-auto">
                    <button
                      onClick={() => navigate(`/projects/${project.id}`)}
                      className="px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors flex-1 sm:flex-none justify-center font-medium"
                    >
                      View details
                    </button>
                    {project.status === 'Pending Approval' && (
                      <>
                        <button
                          onClick={() => handleApproval(project)}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex-1 sm:flex-none justify-center font-medium shadow-sm"
                        >
                          Review & Approve
                        </button>
                        <button
                          onClick={() => quickReject(project.id)}
                          disabled={rejectingId === project.id}
                          className="px-4 py-2 border border-red-200 text-red-600 hover:bg-red-50 rounded-lg transition-colors flex-1 sm:flex-none justify-center font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {rejectingId === project.id ? 'Rejecting…' : 'Quick Reject'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )
        })}

        {filteredRequests.length === 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-12 text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="mt-4 text-lg font-medium text-gray-900">{t('no_requests_found')}</h3>
              <p className="mt-2 text-gray-600">
                {selectedStatus === 'Pending Approval'
                  ? 'No pending funding requests at this time'
                  : `No requests with status "${selectedStatus}"`
                }
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Approval Modal */}
      {showApprovalModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-screen overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">
                Review Funding Request - {selectedRequest.name}
              </h3>
              <button
                onClick={() => setShowApprovalModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-6">
              {/* Request Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">{t('request_summary')}</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">{t('requested_amount')}</p>
                    <p className="font-semibold text-gray-900">${(selectedRequest.totalBudget || 0).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">{t('expected_roi')}</p>
                    <p className="font-semibold text-gray-900">15% (Est)</p>
                  </div>
                  <div>
                    <p className="text-gray-600">{t('payment_schedule')}</p>
                    <p className="font-semibold text-gray-900">{t('net30')}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">{tc('labels.priority')}</p>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(selectedRequest.priority)}`}>
                      {selectedRequest.priority || 'Medium'}
                    </span>
                  </div>
                </div>
              </div>

              {/* decision */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">decision</label>
                <div className="space-y-2">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="decision"
                      value="approve"
                      checked={approvalData.decision === 'approve'}
                      onChange={(e) => setApprovalData({ ...approvalData, decision: e.target.value })}
                      className="mr-2 h-4 w-4 text-green-600 focus:ring-green-500"
                    />
                    <span className="text-green-700 font-medium">✓ Approve Request</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="decision"
                      value="reject"
                      checked={approvalData.decision === 'reject'}
                      onChange={(e) => setApprovalData({ ...approvalData, decision: e.target.value })}
                      className="mr-2 h-4 w-4 text-red-600 focus:ring-red-500"
                    />
                    <span className="text-red-700 font-medium">✗ Reject Request</span>
                  </label>
                </div>
              </div>

              {/* Comments */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Comments {approvalData.decision === 'reject' && <span className="text-red-500">*</span>}
                </label>
                <textarea
                  value={approvalData.comments}
                  onChange={(e) => setApprovalData({ ...approvalData, comments: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t('ph_decision_notes')}
                  required={approvalData.decision === 'reject'}
                />
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                <button
                  onClick={() => setShowApprovalModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={submitApproval}
                  disabled={submitting}
                  className={`px-4 py-2 text-white rounded-lg transition-colors font-medium shadow-sm disabled:opacity-60 disabled:cursor-not-allowed ${approvalData.decision === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
                    }`}
                >
                  {submitting
                    ? 'Processing…'
                    : (approvalData.decision === 'approve' ? '✓ Approve Request' : '✗ Reject Request')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default FundingApproval
